import { UploadFile } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import { allImageFileTypes, imageFileTypes } from './utils';

interface IManyFilesUpload {
  handleFileChange: (val: File[]) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  showSource?: boolean;
  handleUploadClick?: (e: any) => void;
  isLoading?: boolean;
  heading?: string;
  uploadText?: string;
  imageKey: string;
  single?: boolean;
  accept: string;
}

export const ManyFilesUpload: FC<IManyFilesUpload> = ({
  handleUploadClick,
  handleFileChange,
  isLoading,
  isDisabled = false,
  isRequired = false,
  showSource = true,
  heading,
  uploadText,
  imageKey,
  single = false,
  accept
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const handleCapture = async (target: EventTarget & HTMLInputElement) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const allFiles: File[] = Array.from(target.files);
        for (let i = 0; i < allFiles.length; i++) {
          const file = allFiles[i];
          let acceptedFileTypes: string[] = [];
          // file.type is in the format of 'image/png' or 'application/pdf' or 'text/csv'
          const uploadedFileType = file.type.split?.('/')?.[1];
          if (accept.includes('image') && !accept.includes('pdf')) {
            acceptedFileTypes = imageFileTypes;
          }
          if (accept.includes('pdf') && !accept.includes('image')) {
            acceptedFileTypes = ['.pdf'];
          }
          if (accept.includes('pdf') && accept.includes('image')) {
            acceptedFileTypes = allImageFileTypes;
          }
          if (!acceptedFileTypes.includes(`.${uploadedFileType}`)) {
            return enqueueSnackbar(
              `Invalid file type, .${uploadedFileType}. Please upload one of these image types, ${acceptedFileTypes.join(
                ', '
              )}.`,
              {
                variant: 'error'
              }
            );
          }
        }
        handleFileChange(allFiles);
      }
    }
  };

  return (
    <div>
      {heading && (
        <Typography variant="h6" className={classes.heading}>
          {heading}
          {isRequired && '*'}
        </Typography>
      )}
      {isRequired && heading && (
        <Typography variant="caption" className={classes.imageHelpText}>
          Uploading an image is required.
        </Typography>
      )}
      <input
        accept={accept}
        disabled={isDisabled}
        className={classes.input}
        id={`icon-button-file-${imageKey || ''}`}
        type="file"
        multiple={!single}
        onChange={e => {
          handleCapture(e.target);
          // reset the input value so you can re-upload the same photo if you want to
          e.target.value = '';
        }}
      />
      <Box>
        <label htmlFor={`icon-button-file-${imageKey || ''}`}>
          <LoadingButton
            loading={isLoading}
            loadingPosition="start"
            onClick={handleUploadClick}
            disabled={isDisabled}
            startIcon={<UploadFile />}
            size={'large'}
            component={'span'}
          >
            {isLoading ? 'Uploading...' : uploadText ?? 'Upload File(s)'}
          </LoadingButton>
        </label>
      </Box>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    marginTop: theme.spacing(1)
  },
  input: {
    display: 'none'
  },
  imageHelpText: {
    color: theme.palette.error.main
  }
}));
